//#region Angular Import

import { IServiceProvider } from 'angular'

//#endregion

//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from '../../modules/api/api.models'
import { Enums } from '../../models/common'
import { OpLocationEquipment, OpLocation, OpLocationType } from '../../models/core'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'

import { BulkEditInitParams, CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { LocalizationHelper, ToastHelper, ConvertHelper, LogHelper, CommonHelper, Dictionary, KeyType } from '../../helpers'

import { GridService, GRID_SERVICE_NAME } from '../grid'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'
import { ItemSwapperPopupOptions, IItemSwapperPopup, ItemSwapperResult } from '../../modules/common/item-swapper/'
import { ItemSwapperGetObjectIdArgs } from '../../modules/common/item-swapper/item-swapper'
import { ColumnInfo } from '../../models/custom'
import { CreateEditResult, DetailsOptions, DetailsResult, SystemService, SYSTEM_SERVICE_NAME } from '../system'


//#region Public Types

export const DEPOSITORY_SERVICE_NAME = 'DepositoryService'
export const DEPOSITORY_SERVICE_PROVIDER_NAME = 'DepositoryServiceProvider'

//#endregion


export class DepositoryService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Depository]' }
    public static get FILE_NAME_PREFIX(): string { return 'depositories' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdLocation }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Depository }

    //#endregion

    //#region Ctor

    constructor(
        private readonly gridService: GridService,
        private readonly systemService: SystemService,
        private readonly userSessionService: UserSessionService,
    ) { }

    //#endregion

    //#region CreateEdit

    public openBulkEdit(columns: any[], selectedGridItems: any[]): JQueryPromise<CreateEditResult<OpLocation>> {
        const bulkEditInitParams: BulkEditInitParams = this.getBulkEditInitOptions(columns, selectedGridItems)

        return this.systemService.openBulkCreateEdit(bulkEditInitParams);
    }

    public openCreate(): JQueryPromise<CreateEditResult<OpLocation>> {
        return this.getAvailableLocationTypes(Enums.ReferenceType.IdLocationType)
            .then((locationTypes: Dictionary<OpLocationType[]>) => {
                const createEditOptions: CreateEditInitParams = this.getCreateEditOptions()
                createEditOptions.customDataSources = locationTypes

                return this.systemService.openCreateEdit<OpLocation>(createEditOptions)
            })
    }

    public openEdit(idDepository: number): JQueryPromise<CreateEditResult<OpLocation>> {
        return this.getAvailableLocationTypes(Enums.ReferenceType.IdLocationType)
            .then((locationTypes: Dictionary<OpLocationType[]>) => {
                const createEditOptions: CreateEditInitParams = this.getCreateEditOptions(idDepository);
                createEditOptions.customDataSources = locationTypes

                return this.systemService.openCreateEdit<OpLocation>(createEditOptions)
            })
    }

    public openEditByDepository(idDepository: number, depository: OpLocation): JQueryPromise<CreateEditResult<OpLocation>> {
        if (depository != null) {
            return this.getAvailableLocationTypes(Enums.ReferenceType.IdLocationType)
                .then((locationTypes: Dictionary<OpLocationType[]>) => {
                    const createEditOptions: CreateEditInitParams = this.getCreateEditOptions(idDepository, depository)
                    createEditOptions.customDataSources = locationTypes

                    return this.systemService.openCreateEdit<OpLocation>(createEditOptions)
                })
        }

        return null;
    }

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): CreateEditInitParams {
        const createEditOptions: CreateEditInitParams = {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            idReferenceType: DepositoryService.REFERENCE_TYPE,
            moduleName: DepositoryService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject,
            referenceObjectId,
        }

        if (referenceObjectId > 0 && referenceObject == null) {
            createEditOptions.referenceObjectRequest = () => {
                return this.userSessionService.getDataProvider().Depository.getDepository(referenceObjectId)
            }
        }

        return createEditOptions
    }

    private getAvailableLocationTypes(referenceId: number): JQueryPromise<Dictionary<OpLocationType[]>> {
        return this.userSessionService
            .getDataProvider()
            .System
            .getDictionaries([referenceId])
            .then((referenceDictionary: Dictionary<Dictionary<OpLocationType>>) => {
                const referenceLocationTypes: Dictionary<OpLocationType> = referenceDictionary.tryGetValue(Enums.ReferenceType.IdLocationType)
                const defaultLocationType: OpLocationType = referenceLocationTypes.tryGetValue(Enums.LocationType.DepositoryLocation)

                if (defaultLocationType != null) {
                    return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: [defaultLocationType] }], KeyType.Number)
                } else {
                    return null
                }
            })
    }


    private getBulkEditInitOptions(columns: any[], selectedGridItems: any[]): BulkEditInitParams {
        return {
            gridColumns: columns,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            idReferenceType: DepositoryService.REFERENCE_TYPE,
            moduleName: DepositoryService.SIMAX_MODULE_NAME,
            section: null,
            selectedGridRows: angular.copy(selectedGridItems),
            useBusinessObjects: true
        }
    }

    //#endregion

    //#region Details

    public openDetails(idMeter: number, pageItemsIds: number[]): JQueryPromise<DetailsResult> {
        const detailsOptions: DetailsOptions = this.getDetailsOptions(idMeter);

        detailsOptions.pageItemsIds = pageItemsIds;

        return this.systemService.openDetails(detailsOptions);
    }

    public getDetailsOptions(idDepository: number): DetailsOptions {
        return {
            hasPermission: true,
            moduleName: DepositoryService.SIMAX_MODULE_NAME,
            pageItemsIds: null,
            referenceObjectId: idDepository,
        }
    }

    //#endregion

    //#region Rewarehousing

    public getDefaultRewarehousingItemSwapperOptions(idDepository: number = null): JQueryPromise<ItemSwapperPopupOptions<OpLocationEquipment>> {
        const contentColumnsPromise = this.gridService.getColumnModelByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__CONTENT_GRID_PARAMS,
            DepositoryService.SIMAX_MODULE_NAME,
            {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
                viewReferenceType: Enums.ReferenceType.SerialNbr
            }
        )

        const defaultColumnsPromise = this.gridService.getColumnModelByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS,
            DepositoryService.SIMAX_MODULE_NAME,
            {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor,
            }
        )

        return $.when(contentColumnsPromise, defaultColumnsPromise).then((deviceColumns: ColumnModel[], depositoryColumns: ColumnModel[]): ItemSwapperPopupOptions<OpLocationEquipment> => {
            const itemSwapperOptions: ItemSwapperPopupOptions<OpLocationEquipment> = {
                itemSwapperOptions: {
                    rightSelectGridOptions: {
                        columnsDictionary: depositoryColumns,
                        getDataSourceFilter: this.userSessionService.getDataProvider().Depository.getDepositoryFilter,
                    },

                    rightGridOptions: {
                        title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__AVAILABLE, DepositoryService.SIMAX_MODULE_NAME),

                        columnsDictionary: deviceColumns,
                        dataSourceParser: this.getLocationEquipmentParser(),
                        getDataSourceFilter: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
                            if (idDepository != null) {
                                return this.userSessionService
                                    .getDataProvider()
                                    .Depository
                                    .getDepositoryContentFilter(idDepository, columns, pageNbr, pageSize, customWhereClause)
                            } else {
                                return null
                            }
                        }
                    },

                    leftGridOptions: {
                        title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CURRENT, DepositoryService.SIMAX_MODULE_NAME),
                        dataSourceId: idDepository,

                        columnsDictionary: deviceColumns,
                        dataSourceParser: this.getLocationEquipmentParser(),
                        getDataSourceFilter: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
                            if (idDepository != null) {
                                return this.userSessionService
                                    .getDataProvider()
                                    .Depository
                                    .getDepositoryContentFilter(idDepository, columns, pageNbr, pageSize, customWhereClause)
                            } else {
                                return null
                            }
                        }
                    },

                    getObjectId: (e: ItemSwapperGetObjectIdArgs<OpLocationEquipment>) => {
                        return e.object.idLocationEquipment
                    }
                },

                itemSwapperPopupTitle: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONTENT_EDITING, Enums.SimaxModuleName.Depository),

                onInitialized: (itemSwapper: IItemSwapperPopup<OpLocationEquipment>) => {
                    return itemSwapper
                },
            }

            return itemSwapperOptions
        })
    }

    public getLocationEquipmentParser() {
        return (locationEquipment: OpLocationEquipment) => {
            return {
                sourceObject: locationEquipment.device
            }
        }
    }

    public saveDepositoryEquipment(depositoryId: number, swapResult: ItemSwapperResult<OpLocationEquipment>): JQueryPromise<any> {
        const locationEquipmentToChange: OpLocationEquipment[] = []

        swapResult.addedToLeft.forEach((addedItem: OpLocationEquipment) => {
            // Remove from existing location
            const removedEquipment = new OpLocationEquipment(null)

            removedEquipment.idLocationEquipment = addedItem.idLocationEquipment
            removedEquipment.idLocation = addedItem.idLocation
            removedEquipment.idMeter = addedItem.idMeter
            removedEquipment.serialNbr = addedItem.serialNbr
            removedEquipment.startTime = addedItem.startTime
            removedEquipment.opState = Enums.OpChangeState.Delete

            locationEquipmentToChange.push(removedEquipment)

            // Move into new location
            const newEquipment = new OpLocationEquipment(null)

            newEquipment.idLocation = depositoryId
            newEquipment.serialNbr = addedItem.serialNbr
            newEquipment.idMeter = addedItem.idMeter
            newEquipment.startTime = new Date()
            newEquipment.opState = Enums.OpChangeState.New

            locationEquipmentToChange.push(newEquipment)
        })

        swapResult.addedToRight.forEach((removedItem: OpLocationEquipment) => {
            // Remove from existing location
            const removedEquipment = new OpLocationEquipment(null)

            removedEquipment.idLocationEquipment = removedItem.idLocationEquipment
            removedEquipment.idLocation = removedItem.idLocation
            removedEquipment.serialNbr = removedItem.serialNbr
            removedEquipment.idMeter = removedItem.idMeter
            removedEquipment.startTime = removedItem.startTime
            removedEquipment.opState = Enums.OpChangeState.Delete

            locationEquipmentToChange.push(removedEquipment)

            // Move into new location
            const newEquipment = new OpLocationEquipment(null)

            newEquipment.idLocation = swapResult.selectedRightItem.id
            newEquipment.serialNbr = removedItem.serialNbr
            newEquipment.idMeter = removedItem.idMeter
            newEquipment.startTime = new Date()
            newEquipment.opState = Enums.OpChangeState.New

            locationEquipmentToChange.push(newEquipment)
        })

        return this.userSessionService
            .getDataProvider().Location
            .changeLocationEquipment(locationEquipmentToChange)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, DepositoryService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, DepositoryService.SIMAX_MODULE_NAME))
                LogHelper.warn(DepositoryService.LOG_PREFIX, `Unable to save depository contnet for location with id ${depositoryId}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion

    //#region Save

    public saveDepository(depository: OpLocation, isBulkSave: boolean = false): JQueryPromise<OpLocation> {
        return this.userSessionService
            .getDataProvider().Depository
            .saveDepository(depository)
            .then((savedObject: OpLocation) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, DepositoryService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.distributorSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, DepositoryService.SIMAX_MODULE_NAME))
                LogHelper.warn(DepositoryService.LOG_PREFIX, `Unable to save depository with id ${depository.idLocation}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion
}


export class DepositoryServiceProvider implements IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        GRID_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        (
            gridService: GridService,
            systemService: SystemService,
            userSessionService: UserSessionService,
        ) => {
            return new DepositoryService(gridService, systemService, userSessionService);
        }]

    //#endregion
}